import { FormProvider, useForm, useFieldArray } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Box } from "@mui/material";
import { format } from "date-fns";
import { isFulfilled } from "@reduxjs/toolkit";

import { Heading1 } from "../../../common/components/styled/styled";
import FormSection from "../../../common/components/functional/formSection/FormSection";
import { FormContainer } from "../../customer/customerManagement/styles";
import { BaseInfo } from "./BaseInfo";
import { useAppDispatch } from "../../../app/hooks";
import { fetchTesPriceList, updateTesPriceList } from "../settingsActions";
import { isoFormat } from "../../../common/helpers/formatDateTime";
import { PricingFormContainer } from "./PricingFormContainer";
import useMessage from "../../../hooks/useMessage";
import { UpdateTesPriceList } from "../settingsApi";
import { ConfirmationDialog } from "../../../common/components/functional/confirmationDialog/ConfirmationDialog";
import { useState } from "react";
import OverlaySpinner from "../../../common/components/functional/overlaySpinner/OverlaySpinner";

export interface PricingFormValues {
  validFrom: string;
  prices: {
    unitValue: string;
    unit: string;
    unitValueOverrideAllowed: boolean;
    name: string;
  }[];
  version: number | null;
  tesId: string | null;
}

export const SupplementPricing = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { sendMessage } = useMessage();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] =
    useState<boolean>(false);

  const defaultValues: PricingFormValues = {
    validFrom: "",
    prices: [],
    version: null,
    tesId: null,
  };

  const methods = useForm({
    defaultValues,
    mode: "onChange",
  });

  const { control, watch, reset, formState, handleSubmit } = methods;

  const { fields, update } = useFieldArray({
    control,
    name: "prices",
    rules: {
      validate: (value) => {
        return value.every(
          (v) =>
            v.unitValue !== "" &&
            (Number(v.unitValue) || Number(v.unitValue) === 0),
        );
      },
    },
  });

  const prices = watch("prices");
  const isValid = formState.isValid;
  const saveDisabled =
    Object.keys(formState.errors).length > 0 || !isValid || !formState.isDirty;

  const fetchPricings = (date: Date, tesId: string) => {
    setIsLoading(true);
    dispatch(
      fetchTesPriceList({
        tesId,
        date: format(date, isoFormat),
      }),
    ).then((action) => {
      if (isFulfilled(action)) {
        let prices: any[] = [];
        action.payload.forEach((list) => {
          if (tesId && list.tesId) {
            // Manage TES specific pricing
            for (const [key, value] of Object.entries(list.prices)) {
              prices.push({ ...value, name: key });
            }
            reset({
              prices,
              validFrom: list.validFrom,
              version: list.version,
              tesId: list.tesId,
            });
          } else if (tesId === "" && !list.tesId) {
            // Manage global pricing
            for (const [key, value] of Object.entries(list.prices)) {
              prices.push({ ...value, name: key });
            }
            reset({
              prices,
              validFrom: list.validFrom,
              version: list.version,
              tesId: null,
            });
          }
        });
      }
      setIsLoading(false);
    });
  };

  const saveChanges = (fields: any) => {
    setIsConfirmationDialogOpen(false);
    setIsLoading(true);
    const formattedPriceList = fields.prices.reduce((acc: any, price: any) => {
      acc[price.name] = {
        unitValue: price.unitValue,
        unit: price.unit,
        unitValueOverrideAllowed: price.unitValueOverrideAllowed,
      };
      return acc;
    }, {});

    // If validFrom date has changed, omit version number
    const payload: UpdateTesPriceList = {
      tesId: fields.tesId,
      validFrom: fields.validFrom,
      prices: formattedPriceList,
      version: formState.dirtyFields.validFrom ? null : fields.version,
    };

    dispatch(updateTesPriceList(payload)).then((action) => {
      setIsLoading(false);
      if (isFulfilled(action)) {
        reset({
          ...fields,
          version: action.payload[0].version,
        });
        sendMessage(t("settings:pricingUpdated"), "success");
      }
    });
  };

  return (
    <Box>
      <OverlaySpinner isLoading={isLoading} />
      <FormContainer maxWidth="70%">
        <Heading1>{t("settings:supplementPricing")}</Heading1>
        <FormSection noBorder disablePaddingBottom>
          <BaseInfo fetchTesPriceList={fetchPricings} />
          {prices && prices.length > 0 ? (
            <FormProvider {...methods}>
              <PricingFormContainer
                fields={fields}
                saveDisabled={saveDisabled}
                saveChanges={() => setIsConfirmationDialogOpen(true)}
                update={update}
              />
            </FormProvider>
          ) : null}
        </FormSection>
      </FormContainer>
      <ConfirmationDialog
        title={t("settings:confirmSave")}
        contentText={t("settings:confirmSaveText")}
        cancelText={t("common:cancel")}
        confirmText={t("common:save")}
        isOpen={isConfirmationDialogOpen}
        onConfirm={handleSubmit(saveChanges)}
        onCancel={() => setIsConfirmationDialogOpen(false)}
      />
    </Box>
  );
};
